import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import { mobilePaymentService } from '../services/mobilePayment';

const MobilePaymentStatus = ({ show = false }) => {
  const [status, setStatus] = useState('');
  const [level, setLevel] = useState('info');
  const [debugLog, setDebugLog] = useState([]);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    // Check debug mode initially and set up periodic check
    const checkDebugMode = () => {
      const isDebugEnabled = mobilePaymentService.isDebugModeEnabled();
      setDebugMode(isDebugEnabled);
      
      // Show help message in console when debug mode is disabled
      if (!isDebugEnabled) {
        console.log('[MobilePaymentStatus] Debug mode is disabled. To enable debug UI, call: mobilePaymentService.enableDebugMode()');
      }
    };
    
    checkDebugMode();
    
    // Check debug mode every second in case it changes
    const debugInterval = setInterval(checkDebugMode, 1000);

    // Set up status callback
    mobilePaymentService.setStatusCallback((message, statusLevel) => {
      setStatus(message);
      setLevel(statusLevel);
      
      // Update debug log from localStorage
      const log = localStorage.getItem('mobilePaymentDebugLog');
      if (log) {
        try {
          const parsed = JSON.parse(log);
          setDebugLog(parsed.slice(-5)); // Show last 5 entries
        } catch (e) {
          // Ignore parse errors
        }
      }
    });

    return () => {
      clearInterval(debugInterval);
      mobilePaymentService.setStatusCallback(null);
    };
  }, []);

  // Don't show if debug mode is disabled in the service, regardless of show prop
  if (!show || !debugMode) return null;

  const getStatusColor = () => {
    switch (level) {
      case 'success': return '#4caf50';
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      default: return '#2196f3';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 20,
        left: 20,
        right: 20,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        p: 2,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        zIndex: 9999,
        border: `2px solid ${getStatusColor()}`,
        backdropFilter: 'blur(10px)',
        maxHeight: '300px',
        overflow: 'auto'
      }}
    >
      {/* Current Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Chip 
          label={level.toUpperCase()} 
          size="small" 
          sx={{ 
            bgcolor: getStatusColor(), 
            color: 'white',
            mr: 1,
            fontSize: '0.7rem'
          }} 
        />
        <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
          {status || 'Monitoring payment...'}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
          {new Date().toLocaleTimeString()}
        </Typography>
      </Box>

      {/* Progress Bar for Active Operations */}
      {level === 'info' && (
        <LinearProgress 
          sx={{ 
            mb: 1, 
            '& .MuiLinearProgress-bar': { 
              bgcolor: getStatusColor() 
            } 
          }} 
        />
      )}

      {/* Recent Debug Log */}
      <Box sx={{ maxHeight: 80, overflow: 'auto', mb: 1 }}>
        {debugLog.map((entry, index) => (
          <Typography 
            key={index}
            variant="caption" 
            sx={{ 
              display: 'block', 
              color: 'text.secondary',
              fontSize: '0.65rem',
              fontFamily: 'monospace'
            }}
          >
            {new Date(entry.timestamp).toLocaleTimeString()}: {entry.message}
          </Typography>
        ))}
      </Box>

      {/* System Status */}
      <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
          System Status:
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', display: 'block' }}>
          Payment In Progress: {window.mobilePaymentService?.paymentInProgress ? 'Yes' : 'No'} | 
          Polling Active: {window.mobilePaymentService?.pollInterval ? 'Yes' : 'No'} | 
          Callback Processing: {window.mobilePaymentService?.processingCallback ? 'Yes' : 'No'}
        </Typography>
      </Box>

      {/* Debug Helper */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            flex: 1,
            color: 'text.disabled',
            fontSize: '0.6rem',
            cursor: 'pointer'
          }}
          onClick={() => {
            // Copy debug info to clipboard on mobile
            const debugInfo = JSON.stringify(debugLog, null, 2);
            if (navigator.clipboard) {
              navigator.clipboard.writeText(debugInfo).catch(() => {
                // Fallback: show alert with info
                alert(`Debug Info:\n${debugInfo}`);
              });
            } else {
              alert(`Debug Info:\n${debugInfo}`);
            }
          }}
        >
          ColorOS Debug • Tap to copy
        </Typography>
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#2196f3',
            fontSize: '0.6rem',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={() => {
            if (window.testMobilePaymentPolling) {
              window.testMobilePaymentPolling();
            }
          }}
        >
          Test Polling
        </Typography>
      </Box>
    </Box>
  );
};

export default MobilePaymentStatus;